import { useNavigate } from "react-router-dom";
import { Home as HomeIcon, Target, Star, Sparkles, ShoppingCart, BookOpen, HandHeart } from "lucide-react";
import { LOGO_URL } from "@/config";
import formatCurrency from "@/utils/formatCurrency";
import CurrencyInput from "@/components/shared/CurrencyInput";

const BudgetPlanner = ({ monthlySalary, budgetBuckets, onUpdateSalary, onUpdateBuckets, user, onLogout }) => {
  const navigate = useNavigate();

  const bucketIcons = {
    "Financial Freedom": Target, "Future Goals": Star, "Recreation": Sparkles,
    "Needs & Lifestyle": ShoppingCart, "Learning": BookOpen, "Charity": HandHeart,
  };

  const handlePercentageChange = (index, newPercentage) => {
    const updated = [...budgetBuckets];
    updated[index] = { ...updated[index], percentage: newPercentage, budget: (monthlySalary * newPercentage) / 100 };
    onUpdateBuckets(updated);
  };

  const handleSalaryChange = (value) => {
    onUpdateSalary(value);
    const updated = budgetBuckets.map((bucket) => ({ ...bucket, budget: (value * bucket.percentage) / 100 }));
    onUpdateBuckets(updated);
  };

  return (
    <div className="finance-app min-h-screen p-3 sm:p-6 lg:p-8 flex items-center justify-center" data-testid="budget-planner-page">
      <div className="w-full max-w-2xl">
        <div className="finance-card">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="SubiWealth" className="h-10" />
              <h2 className="text-xl font-semibold text-gray-800">Monthly Salary</h2>
            </div>
            <CurrencyInput
              value={monthlySalary}
              onChange={(v) => handleSalaryChange(v)}
              className="w-48 px-4 py-2 text-right rounded-lg bg-gray-50 border border-gray-200 focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
            />
          </div>

          <div className="table-scroll">
          <table className="w-full budget-table min-w-[400px]" data-testid="budget-table">
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Bucket</th>
                <th>% Income</th>
                <th className="text-right">Budget</th>
              </tr>
            </thead>
            <tbody>
              {budgetBuckets.map((bucket, index) => {
                const IconComponent = bucketIcons[bucket.name] || Target;
                return (
                  <tr key={bucket.name} data-testid={`budget-row-${index}`}>
                    <td className="text-gray-500">{index + 1}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <IconComponent size={18} className="text-teal-600" />
                        <span className="font-medium">{bucket.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          data-testid={`bucket-percentage-${index}`}
                          className="percentage-input"
                          value={bucket.percentage || ""}
                          onChange={(e) => handlePercentageChange(index, parseFloat(e.target.value) || 0)}
                          min="0" max="100"
                        />
                        <span className="text-gray-500">%</span>
                      </div>
                    </td>
                    <td className="text-right font-medium text-gray-700" data-testid={`bucket-budget-${index}`}>
                      ₹{formatCurrency(bucket.budget)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
            <button data-testid="budget-home-btn" className="nav-btn primary" onClick={() => navigate("/")}>
              <HomeIcon size={18} className="inline mr-2" />
              Home
            </button>
            <div className="text-right">
              <span className="text-gray-500 text-sm">Total Allocated</span>
              <p className="text-lg font-bold text-teal-600" data-testid="total-allocated">
                {budgetBuckets.reduce((sum, b) => sum + b.percentage, 0)}%
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetPlanner;
